export const normalizePersonName = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const buildApplicantDisplayName = (formData = {}) => {
  const parts = [
    formData.firstName,
    formData.middleName,
    formData.lastName,
    formData.suffix,
  ].filter((part) => String(part || '').trim() !== '');

  return parts.join(' ').replace(/\s+/g, ' ').trim();
};

export const govIdNameMatchesApplicant = (applicant = {}, nameOnId = '') => {
  const normalizedIdName = normalizePersonName(nameOnId);
  if (!normalizedIdName) return false;

  const firstName = normalizePersonName(applicant.firstName);
  const lastName = normalizePersonName(applicant.lastName);

  if (!firstName || !lastName) return false;

  const firstToken = firstName.split(' ')[0];
  if (!normalizedIdName.includes(firstToken) || !normalizedIdName.includes(lastName)) {
    return false;
  }

  const middleName = normalizePersonName(applicant.middleName);
  if (middleName) {
    const middleToken = middleName.split(' ')[0];
    const middleInitial = middleToken.charAt(0);
    const hasMiddle =
      normalizedIdName.includes(middleToken) ||
      (middleInitial && new RegExp(`\\b${middleInitial}\\b`).test(normalizedIdName));
    if (!hasMiddle) return false;
  }

  const suffix = normalizePersonName(applicant.suffix);
  if (suffix && !normalizedIdName.includes(suffix)) {
    return false;
  }

  return true;
};

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const isValidGovIdNumber = (docType, value) => {
  const digits = digitsOnly(value);
  switch (docType) {
    case 'tin':
      return digits.length === 9 || digits.length === 12;
    case 'sss':
      return digits.length === 10;
    case 'philhealth':
      return digits.length === 12;
    case 'pagibig':
      return digits.length === 12;
    default:
      return false;
  }
};

const GOV_ID_FIELDS = [
  { key: 'tin', numberField: 'tinNumber', nameField: 'tinNameOnId', label: 'TIN' },
  { key: 'sss', numberField: 'sssNumber', nameField: 'sssNameOnId', label: 'SSS' },
  { key: 'philhealth', numberField: 'philHealthNumber', nameField: 'philHealthNameOnId', label: 'PhilHealth' },
  { key: 'pagibig', numberField: 'pagIbigNumber', nameField: 'pagIbigNameOnId', label: 'Pag-IBIG' },
];

export const validateGovernmentIdsForApplicant = (formData = {}) => {
  const applicant = {
    firstName: formData.firstName,
    middleName: formData.middleName,
    lastName: formData.lastName,
    suffix: formData.suffix,
  };

  const details = {};
  let verified = true;

  GOV_ID_FIELDS.forEach(({ key, numberField, nameField, label }) => {
    const number = String(formData[numberField] || '').trim();
    const nameOnId = String(formData[nameField] || '').trim();

    // Government IDs are optional — skip when both number and name are blank.
    if (number === '' && nameOnId === '') {
      details[key] = {
        label,
        number: '',
        name_on_id: '',
        number_valid: true,
        name_match: true,
        verified: true,
        skipped: true,
      };
      return;
    }

    const numberValid = isValidGovIdNumber(key, number);
    const nameMatch = govIdNameMatchesApplicant(applicant, nameOnId);
    const itemVerified = number !== '' && nameOnId !== '' && numberValid && nameMatch;

    details[key] = {
      label,
      number,
      name_on_id: nameOnId,
      number_valid: numberValid,
      name_match: nameMatch,
      verified: itemVerified,
      skipped: false,
    };

    if (!itemVerified) verified = false;
  });

  return { verified, details };
};

export const getGovIdValidationErrors = (validation) => {
  if (!validation || validation.verified) return [];

  return Object.values(validation.details || {})
    .filter((item) => !item.verified && !item.skipped)
    .map((item) => {
      if (!item.number) return `${item.label}: number is required`;
      if (!item.name_on_id) return `${item.label}: name on ID is required`;
      if (!item.number_valid) return `${item.label}: invalid number format`;
      if (!item.name_match) return `${item.label}: name on ID does not match your registered name`;
      return `${item.label}: verification failed`;
    });
};
